import { useCallback, useEffect, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import ProjectsPanel from '../projects/ProjectsPanel';
import FundingPanel from '../funding/FundingPanel';
import FunderPanel from '../funders/FunderPanel';
import ApiDocsPanel from '../docs/ApiDocsPanel';
import DocsPanel from '../docs/DocsPanel';
import ReleaseNotesPanel from '../releases/ReleaseNotesPanel';

/** Docs are long-form, so the panel is draggable. Everything else keeps its
 *  fixed shape — a settings panel gains nothing from being resizable. */
const WIDTH_KEY = 'hs_docs_panel_width';
const DEFAULT_W = 736;   // 46rem, the previous fixed width
const MIN_W = 420;
const RAIL_W = 64;       // primary rail, so the drag maths starts at the panel edge

/** Pickers only. Settings, Organizations, Program Increments and Tasks used to
 *  render here; they navigate nowhere, so they left an unrelated page beside
 *  them and are routes now (see `pages/PanelPages.tsx`). Everything remaining
 *  either drives the main area or is meant to be read beside it. */
export default function SlideoutPanel() {
  const { activePanel, setActivePanel } = useAppStore();
  const isOpen = activePanel !== null;
  const resizable = activePanel === 'docs';

  const [width, setWidth] = useState(() => {
    const saved = Number(localStorage.getItem(WIDTH_KEY));
    return Number.isFinite(saved) && saved >= MIN_W ? saved : DEFAULT_W;
  });
  // Mirrored so the mouseup handler can persist without re-binding on every
  // pixel of movement.
  const widthRef = useRef(width);
  widthRef.current = width;
  const dragging = useRef(false);

  const clamp = useCallback(
    (px: number) => Math.min(Math.max(px, MIN_W), Math.max(MIN_W, window.innerWidth - 240)),
    []);

  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!dragging.current) return;
      e.preventDefault();
      setWidth(clamp(e.clientX - RAIL_W));
    };
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      localStorage.setItem(WIDTH_KEY, String(widthRef.current));
    };
    // A saved width from a wider monitor must not leave the panel off-screen.
    const onResize = () => setWidth((w) => clamp(w));

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('resize', onResize);
    };
  }, [clamp]);

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  const resetWidth = () => {
    setWidth(DEFAULT_W);
    localStorage.setItem(WIDTH_KEY, String(DEFAULT_W));
  };

  const renderContent = () => {
    switch (activePanel) {
      case 'projects':
        return <ProjectsPanel />;
      case 'funding':
        return <FundingPanel />;
      case 'funders':
        return <FunderPanel />;
      case 'api-docs':
        return <ApiDocsPanel onClose={() => setActivePanel(null)} />;
      case 'docs':
        return <DocsPanel />;
      case 'release-notes':
        return <ReleaseNotesPanel />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (activePanel) {
      case 'projects': return 'Projects';
      case 'funding': return 'Funding Pipeline';
      case 'funders': return 'Funders';
      case 'api-docs': return 'API Documentation';
      case 'docs': return 'Documentation';
      case 'release-notes': return 'Release Notes';
      default: return '';
    }
  };

  // Width is inline when resizable so it can be an arbitrary pixel value; the
  // transition then animates opacity only, since a class-based width and an
  // inline one would fight each other.
  const panelStyle = resizable ? { width } : undefined;
  const panelClass = resizable ? '' : 'w-80';

  return (
    <Transition show={isOpen}>
      <div className="relative h-screen flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden">
        <Transition.Child
          enter="transition-all duration-200 ease-out"
          enterFrom="w-0 opacity-0"
          enterTo={`${resizable ? '' : 'w-80'} opacity-100`}
          leave="transition-all duration-150 ease-in"
          leaveFrom={`${resizable ? '' : 'w-80'} opacity-100`}
          leaveTo="w-0 opacity-0"
        >
          <div className={`${panelClass} h-screen flex flex-col relative`} style={panelStyle}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 h-14 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{getTitle()}</h2>
              <button
                onClick={() => setActivePanel(null)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {renderContent()}
            </div>

            {/* Drag handle. Wider hit area than its visible line, because a
                4px target is a frustrating one. */}
            {resizable && (
              <div
                onMouseDown={startDrag}
                onDoubleClick={resetWidth}
                title="Drag to resize · double-click to reset"
                className="absolute top-0 right-0 h-full w-2 cursor-col-resize group z-10"
              >
                <div className="absolute inset-y-0 right-0 w-px bg-gray-200 dark:bg-gray-700 group-hover:bg-accent group-hover:w-0.5 transition-all" />
              </div>
            )}
          </div>
        </Transition.Child>
      </div>
    </Transition>
  );
}
